/**
 * Cross-tab measurement edit coordination via BroadcastChannel.
 *
 * Phase 1's per-measurement `actionChannel` serializes writes within a tab,
 * but two browser tabs editing the same reader task in the same browser
 * profile can still race. This utility publishes `edit-start` / `edit-end`
 * events on a same-origin BroadcastChannel keyed by `measurementId`, so each
 * tab can render a "read-only — being edited in another tab" banner instead
 * of issuing a competing PUT.
 *
 * Lifecycle:
 *   - One channel per browser tab, lazily constructed on first use.
 *   - Each tab gets a UUID at module init; messages are tagged with it.
 *   - On tab unload we publish edit-end for every lock this tab still holds.
 *
 * Staleness:
 *   - If a tab crashes mid-edit (no edit-end published), other tabs would
 *     stay read-only indefinitely. To bound this, edit-start carries a
 *     `timestamp` and locks are considered stale after `LOCK_TTL_MS`. The
 *     subscriber filters expired entries when answering "who's editing X?".
 *   - LOCK_TTL_MS is aligned with the 15s window `lockUnlockMeasurementsWorker`
 *     already waits on a create/update PUT, so a slow-but-live append PUT does
 *     not expire its lock early and let another tab race the same record.
 */

import { getUniqTabUUID } from '@restore/tools';

const CHANNEL_NAME = 'd7-measurement-edit';
const LOCK_TTL_MS = 15_000;

export type EditMessageType = 'edit-start' | 'edit-end';

export type EditMessage = {
  type: EditMessageType;
  tabId: string;
  measurementId: string;
  timestamp: number;
};

export type RemoteLock = {
  tabId: string;
  timestamp: number;
};

type Subscriber = (msg: EditMessage) => void;

let tabId: string | null = null;
let channel: BroadcastChannel | null = null;
let subscribers: Set<Subscriber> = new Set();
let remoteLocks: Map<string, RemoteLock> = new Map();
let localHeldLocks: Set<string> = new Set();

const isAvailable = (): boolean =>
  typeof globalThis !== 'undefined' &&
  typeof globalThis.BroadcastChannel !== 'undefined';

const ensureTabId = (): string => {
  if (tabId) return tabId;
  tabId = getUniqTabUUID() ?? `tab-${crypto.randomUUID()}`;
  return tabId;
};

const sweepExpired = (now: number): void => {
  for (const [id, lock] of remoteLocks) {
    if (now - lock.timestamp > LOCK_TTL_MS) remoteLocks.delete(id);
  }
};

const onMessage = (event: MessageEvent<EditMessage>) => {
  const msg = event.data;
  if (!msg || msg.tabId === ensureTabId()) return; // ignore our own echoes
  sweepExpired(Date.now());
  if (msg.type === 'edit-start') {
    remoteLocks.set(msg.measurementId, {
      tabId: msg.tabId,
      timestamp: msg.timestamp,
    });
  } else {
    const existing = remoteLocks.get(msg.measurementId);
    if (existing?.tabId === msg.tabId) {
      remoteLocks.delete(msg.measurementId);
    }
  }
  for (const s of subscribers) s(msg);
};

const ensureChannel = (): BroadcastChannel | null => {
  if (!isAvailable()) return null;
  if (channel) return channel;
  channel = new globalThis.BroadcastChannel(CHANNEL_NAME);
  channel.addEventListener('message', onMessage);
  // Release any locks this tab still holds when the tab goes away.
  if (typeof globalThis.addEventListener === 'function') {
    globalThis.addEventListener('beforeunload', () => {
      for (const id of localHeldLocks) {
        try {
          channel?.postMessage({
            type: 'edit-end',
            tabId: ensureTabId(),
            measurementId: id,
            timestamp: Date.now(),
          });
        } catch {
          // Best-effort on unload.
        }
      }
    });
  }
  return channel;
};

/**
 * Publish an `edit-start` for `measurementId`. Other tabs subscribed to the
 * channel will see the message and can switch their UI to read-only.
 */
export const publishEditStart = (measurementId: string): void => {
  const c = ensureChannel();
  if (!c) return;
  localHeldLocks.add(measurementId);
  c.postMessage({
    type: 'edit-start',
    tabId: ensureTabId(),
    measurementId,
    timestamp: Date.now(),
  });
};

/**
 * Publish an `edit-end` for `measurementId`. Safe to call multiple times.
 */
export const publishEditEnd = (measurementId: string): void => {
  const c = ensureChannel();
  if (!c) return;
  localHeldLocks.delete(measurementId);
  c.postMessage({
    type: 'edit-end',
    tabId: ensureTabId(),
    measurementId,
    timestamp: Date.now(),
  });
};

/**
 * Subscribe to cross-tab edit events. Returns an unsubscribe function.
 * Use from forms to render a "being edited in another tab" banner.
 */
export const subscribeToEditEvents = (cb: Subscriber): (() => void) => {
  ensureChannel();
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
};

/**
 * True if another tab currently holds a non-stale edit lock on this
 * measurement. Forms call this to decide whether to disable Add / trash
 * controls when no local edit is in flight.
 */
export const isLockedByRemoteTab = (measurementId: string): boolean => {
  const lock = remoteLocks.get(measurementId);
  if (!lock) return false;
  if (Date.now() - lock.timestamp > LOCK_TTL_MS) {
    remoteLocks.delete(measurementId);
    return false;
  }
  return true;
};

/**
 * Test-only: wipe channel state. Used in vitest to keep cases isolated.
 */
export const _resetForTests = (): void => {
  channel?.close?.();
  channel = null;
  tabId = null;
  subscribers = new Set();
  remoteLocks = new Map();
  localHeldLocks = new Set();
};
